import { Box, Button, Dialog, DialogActions, DialogContent, DialogContentText, Grid, Paper, TextField, Typography } from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import React, { useEffect, useState, useCallback, useRef } from 'react';
import api from '../../../services/api/base';
import { IGiftboxStory } from '../../../services/types';
import GiftboxStoryList from './GiftboxStoryList';
import useStyles from './styles';
import useConfirmDialog from '../../../hooks/useConfirmDialog';
import useBackdrop from '../../../hooks/useBackdrop';
import useSnackbar from '../../../hooks/useSnackbar';
import { Canceler } from 'axios';
import { updateGiftBox, getGiftboxById } from '../../../services/api/Giftbox';

interface IProps {
  giftboxId: number;
  onEdit?: () => void;
}

const EditGiftBox = (props: IProps): JSX.Element => {
  const { giftboxId } = props;
  const classes = useStyles();
  const [giftboxTitle, setGiftBoxTitle] = useState<string>('');
  const [giftboxStories, setGiftboxStories] = useState<IGiftboxStory[]>([]);
  const [validGiftboxStoryNumber, setValidGiftboxStoryNumber] = useState<number>(0);
  const [isLive, setIsLive] = useState<boolean>();

  const confirm = useConfirmDialog();
  const backdrop = useBackdrop();
  const snackbar = useSnackbar();

  const canceler = useRef<Canceler>(() => undefined);

  const addStory = () => {
    const clonedGiftboxStories = [...giftboxStories];
    const newGiftboxStories = clonedGiftboxStories.concat([
      {
        storyId: '',
        numberOfCoupons: '',
        validityPeriod: '',
        isValid: false,
      },
    ]);
    setGiftboxStories([...newGiftboxStories]);
  };

  const saveGiftbox = () => {
    const onProceed = async () => {
      const validGiftboxStories = giftboxStories
        .filter(gs => gs.isValid)
        .map((gs, index) => {
          return {
            id: gs.id,
            storyId: Number(gs.storyId),
            numberOfCoupons: Number(gs.numberOfCoupons),
            validityPeriod: Number(gs.validityPeriod),
            order: index,
          };
        });

      if (validGiftboxStories.length < 7) {
        setOpenDialog(true);
        return;
      }

      try {
        backdrop.show();
        await updateGiftBox(giftboxId, {
          title: giftboxTitle,
          giftboxStories: validGiftboxStories,
        });

        snackbar.success('Saved changes successfully.');

        if (props.onEdit) {
          props.onEdit();
        }

        fetchData();
      } catch (e) {
        snackbar.error(`An error has occurred. ${e.message}`);
      } finally {
        backdrop.hide();
      }
    };

    confirm.open({
      onProceed,
    });
  };

  useEffect(() => {
    setValidGiftboxStoryNumber(giftboxStories.filter(gs => gs.isValid).length);
  }, [giftboxStories]);

  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const handleDialogClose = () => {
    setOpenDialog(false);
  };

  const fetchData = useCallback(async () => {
    canceler.current();

    try {
      const [fetchPromise, cancel] = await getGiftboxById(giftboxId);
      canceler.current = cancel;
      const result = await fetchPromise;

      setGiftBoxTitle(result.data.title);
      const giftboxStories = result.data.stories;

      for (const giftboxStory of giftboxStories) {
        giftboxStory.storyId = `${giftboxStory.storyId}`;
        giftboxStory.isValid = true;
      }

      setGiftboxStories(giftboxStories);
      setIsLive(result.data.isLive);
    } catch (e) {
      if (api.isCancel(e)) return;
    }
  }, [giftboxId]);

  useEffect(() => {
    fetchData();

    return () => canceler.current();
  }, [fetchData]);

  return (
    <Paper className={classes.editRoot}>
      <Box p={2}>
        <Grid container>
          <Grid item xs={12}>
            <Box display="flex" justifyContent="flex-end" alignItems="center">
              {validGiftboxStoryNumber < 7 ? <Typography className={classes.minimumStoryText}>At least 7 stories required</Typography> : ''}
              <Button variant="contained" color="primary" onClick={saveGiftbox}>
                SUBMIT
              </Button>
            </Box>
          </Grid>
        </Grid>
        <Grid container>
          <Grid item xs={10} className={classes.giftboxTitleGrid}>
            <TextField
              label="Gift Box Title"
              fullWidth
              value={giftboxTitle}
              onChange={data => {
                const input = data.target.value as string;
                if (input.length <= 50) setGiftBoxTitle(data.target.value as string);
              }}
              InputProps={{ classes: { underline: classes.underline } }}
            />
            <Typography>{giftboxTitle.length}/50</Typography>
          </Grid>
        </Grid>
      </Box>
      <GiftboxStoryList giftboxStories={giftboxStories} setGiftboxStories={setGiftboxStories} isLive={isLive === true} />
      <Box p={2}>
        <Button color="primary" onClick={addStory}>
          ADD STORY
          <AddIcon />
        </Button>
      </Box>
      <Dialog open={openDialog} onClose={handleDialogClose} aria-labelledby="alert-dialog-title" aria-describedby="alert-dialog-description">
        <DialogContent>
          <DialogContentText id="alert-dialog-description">You need to add at least 7 stories to create a Gift Box.</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose}>Okay</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default EditGiftBox;
